import { useState, useEffect, useMemo } from "react"
import { UserPlus, User, X, Check, Users, Hand, Monitor, Files, Presentation, Video, PenLine } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { cn } from "@vc/lib/utils"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

interface JoinRequest {
  id: string
  name: string
  title: string
}

/** What someone in the meeting asked a moderator for. */
export type PermissionRequestKind = "screenShare" | "document" | "whiteboard" | "video" | "annotation"

export interface PermissionRequest {
  id: string
  name: string
  kind: PermissionRequestKind
  /** The account email of a screen-share page, whose name is whatever was typed on it. */
  email?: string | null
}

const ASKED_FOR: Record<PermissionRequestKind, { icon: typeof Monitor; body: string }> = {
  screenShare: { icon: Monitor, body: "dialogs.presenterRequestBody" },
  document: { icon: Files, body: "dialogs.presenterRequestBodyDocument" },
  whiteboard: { icon: Presentation, body: "dialogs.presenterRequestBodyWhiteboard" },
  video: { icon: Video, body: "dialogs.presenterRequestBodyVideo" },
  annotation: { icon: PenLine, body: "dialogs.annotationRequestBody" },
}

interface JoinRequestsWidgetProps {
  joinRequests: JoinRequest[]
  /**
   * Admit and deny are SEPARATE, and that is the whole point of this shape.
   *
   * This widget used to report only the list it wanted to keep, and both
   * buttons removed the row — so the two were indistinguishable by the time
   * they reached the caller, which admitted everything that had gone. Pressing
   * ✕ to turn somebody away let them straight in, on a room the host had
   * locked. `ParticipantsPanel` has taken these as two callbacks all along.
   */
  onAdmit: (id: string) => void
  onDeny: (id: string) => void
  onAdmitAll: () => void
  /** Requests to present or annotate, answered here as well as in the People list. */
  permissionRequests?: PermissionRequest[]
  onApproveRequest?: (request: PermissionRequest) => void
  onRejectRequest?: (request: PermissionRequest) => void
}

/** One kind of ask on the closed pill: its icon, its count and, on wider screens, its name. */
function PillBadge({ icon: Icon, count, label }: { icon: typeof User; count: number; label: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className="relative">
        <span className="flex h-7 w-7 items-center justify-center rounded-full shadow-md bg-gradient-to-br from-primary to-primary-600 shadow-primary/30">
          <Icon className="h-3.5 w-3.5 text-white" />
        </span>
        <span className="absolute -inset-0.5 rounded-full bg-card/30 animate-ping opacity-55" />
        <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 flex items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold text-white shadow-sm">
          {count}
        </span>
      </span>
      <span className="hidden font-semibold text-white/90 drop-shadow-sm sm:inline">{label}</span>
    </span>
  )
}

/**
 * Everything waiting on a moderator, behind one pill at the top right of the
 * video area: people waiting to join, and requests to present or annotate,
 * in two sections that each scroll on their own. It never opens by itself:
 * a new ask pulses the pill and bumps its count, and the host opens it when
 * ready.
 */
export function JoinRequestsWidget({
  joinRequests,
  onAdmit,
  onDeny,
  onAdmitAll,
  permissionRequests = [],
  onApproveRequest,
  onRejectRequest,
}: JoinRequestsWidgetProps) {
  const t = useTranslation();
  const [isOpen, setIsOpen] = useState(false)
  const [isLandscape, setIsLandscape] = useState(false)
  const total = joinRequests.length + permissionRequests.length
  const hasRequests = total > 0

  // Detect landscape orientation using window dimensions
  useEffect(() => {
    const checkOrientation = () => {
      const width = window.innerWidth
      const height = window.innerHeight
      setIsLandscape(width > height && width < 1024) // Landscape mobile/tablet
    }

    // Check initially
    checkOrientation()

    // Listen for resize events
    window.addEventListener('resize', checkOrientation)

    return () => {
      window.removeEventListener('resize', checkOrientation)
    }
  }, [])

  // Nothing left to answer: close, so the next ask starts from the pill again.
  useEffect(() => {
    if (!hasRequests) setIsOpen(false)
  }, [hasRequests])

  // The host can put the whole thing away (the cross in the panel): everything
  // waiting can still be answered from the Participants list, in its Waiting
  // Room tab and on the asker's row. It stays away for what was waiting when
  // it was closed, and comes back on its own for anything new.
  //
  // Forgotten once nothing is waiting: a request to present is keyed by who
  // asked and what for, so the same person asking again later would otherwise
  // match the old key and stay hidden.
  const waitingKeys = useMemo(
    () => [...joinRequests.map((r) => `lobby:${r.id}`), ...permissionRequests.map((r) => `${r.kind}:${r.id}`)],
    [joinRequests, permissionRequests],
  )
  const [hiddenKeys, setHiddenKeys] = useState<Set<string> | null>(null)
  const hidden =
    hiddenKeys !== null && waitingKeys.length > 0 && waitingKeys.every((k) => hiddenKeys.has(k))
  useEffect(() => {
    if (hiddenKeys !== null && !hidden) setHiddenKeys(null)
  }, [hiddenKeys, hidden])
  const hide = () => {
    setHiddenKeys(new Set(waitingKeys))
    setIsOpen(false)
  }

  // The row leaves the list because the server says so — the host's decision
  // is broadcast back as `lobby:updated`. Dropping it locally as well would
  // hide a decision that failed to reach the server.
  const handleAdmit = (id: string) => onAdmit(id)
  const handleDeny = (id: string) => onDeny(id)
  const handleAdmitAll = () => onAdmitAll()

  // With both kinds waiting, each list gets half the room so neither pushes
  // the other out of view; on its own, a list may use it all.
  const both = joinRequests.length > 0 && permissionRequests.length > 0
  const listHeight = isLandscape
    ? (both ? "max-h-[110px]" : "max-h-[150px]")
    : (both ? "max-h-[170px]" : "max-h-[260px]")

  const rowClass = cn(
    "group flex items-center gap-3 p-3 rounded-xl bg-card border border-slate-200 dark:border-border/60",
    "shadow-sm hover:shadow-md hover:border-slate-300/80",
    "transition-all duration-200 animate-in fade-in slide-in-from-right-2",
  )
  const avatarClass = "flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-slate-100 to-slate-200 dark:from-muted dark:to-muted text-sm font-bold text-slate-700 dark:text-foreground border-2 border-white dark:border-border shadow-sm"
  const denyClass = "h-9 w-9 rounded-lg text-slate-400 dark:text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 p-0 transition-all duration-200"

  return (
    // Placed by the meeting screen, at the top right of the video area.
    // Relative so the list below hangs from the pill.
    <div className="relative">
      {/* Trigger: shown while anything is waiting, pulsing */}
      {hasRequests && !hidden &&
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className={cn(
          "group relative flex items-center gap-3 rounded-full py-1.5 pl-1.5 pr-1.5 sm:pr-3 text-xs font-medium transition-all duration-300",
          "bg-slate-900/20 backdrop-blur-xl shadow-lg shadow-black/20 hover:bg-slate-900/28 hover:shadow-xl hover:shadow-black/25",
          "border border-white/15 ring-1 ring-white/10 hover:border-primary/25",
          "border-white/10 ring-2 ring-primary/20 bg-slate-900/30",
          isOpen && "border-white/10 ring-2 ring-primary/20 bg-slate-900/30"
        )}
      >
        {/* One badge per kind, each with its own count, so a closed pill still
            says whether someone is at the door or asking to present. */}
        {joinRequests.length > 0 && (
          <PillBadge icon={UserPlus} count={joinRequests.length} label={t("overlays.lobby")} />
        )}
        {permissionRequests.length > 0 && (
          <PillBadge icon={User} count={permissionRequests.length} label={t("overlays.requests")} />
        )}
      </button>}

      {/* Dropdown Panel */}
      {isOpen && hasRequests && !hidden && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          <div className="absolute right-0 top-full mt-2 sm:mt-3 w-[280px] sm:w-[320px] md:w-[340px] max-w-[calc(100vw-2rem)] z-50">
            <div className="rounded-2xl bg-pannelcolor shadow-2xl shadow-black/15 border border-slate-200 dark:border-border/80 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="flex justify-end bg-secondary-100 dark:bg-card px-2 pt-2">
                <button
                  type="button"
                  onClick={hide}
                  aria-label={t("common.close")}
                  title={t("common.close")}
                  className="grid h-7 w-7 place-items-center rounded-full text-slate-500 dark:text-muted-foreground transition hover:bg-slate-200 dark:hover:bg-muted hover:text-slate-800 dark:hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Waiting to join */}
              {joinRequests.length > 0 && (
                <>
                  <div className="relative px-5 py-3 bg-secondary-100 dark:bg-card">
                    <div className="relative flex items-center justify-between">
                      <div className="flex items-center text-black dark:text-foreground gap-1">
                        <div className="flex h-8 w-8 items-center justify-center">
                          <Users className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-xs sm:text-sm">{t("overlays.joinRequests")}</h3>
                          <p className="text-xs">{joinRequests.length} {t("overlays.pending")}</p>
                        </div>
                      </div>
                      <Button size="sm" variant={'default'} onClick={handleAdmitAll} className="h-8 px-3">
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                        {t("overlays.admitAll")}
                      </Button>
                    </div>
                  </div>
                  <div className={cn(listHeight, "overflow-y-auto overscroll-contain p-3 space-y-2 bg-slate-50/50 dark:bg-muted/50")}>
                    {joinRequests.map((request, index) => (
                      <div key={request.id} className={rowClass} style={{ animationDelay: `${index * 50}ms` }}>
                        <div className="relative shrink-0">
                          <div className={avatarClass}>{request.name.slice(0, 2).toUpperCase()}</div>
                          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-amber-400 border-2 border-white dark:border-border" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 dark:text-foreground/80 truncate">{request.name}</p>
                          <p className="text-xs text-slate-500 dark:text-muted-foreground truncate">{request.title || t("overlays.guest")}</p>
                        </div>
                        <div className="flex items-center gap-1.5 opacity-80 group-hover:opacity-100 transition-opacity">
                          <Button size="sm" variant="ghost" onClick={() => handleDeny(request.id)} className={denyClass}
                            aria-label={t("participantsPanel.denyRequest")} title={t("participantsPanel.denyRequest")}>
                            <X className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant={'default'} onClick={() => handleAdmit(request.id)} className="h-9 w-9 rounded-lg"
                            aria-label={t("participantsPanel.admitToMeeting")} title={t("participantsPanel.admitToMeeting")}>
                            <Check className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Requests to present or annotate */}
              {permissionRequests.length > 0 && (
                <>
                  <div className={cn("relative px-5 py-3 bg-secondary-100 dark:bg-card", joinRequests.length > 0 && "border-t border-stroke-100 dark:border-border")}>
                    <div className="relative flex items-center justify-between">
                      <div className="flex items-center text-black dark:text-foreground gap-1">
                        <div className="flex h-8 w-8 items-center justify-center">
                          <Hand className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-xs sm:text-sm">{t("overlays.requests")}</h3>
                          <p className="text-xs">{permissionRequests.length} {t("overlays.pending")}</p>
                        </div>
                      </div>
                      {/* Answering several one by one pulls the host out of the
                          meeting each time; one click turns them all down. */}
                      {permissionRequests.length >= 2 && (
                        <Button size="sm" variant="outline" className="h-8 px-3"
                          onClick={() => permissionRequests.forEach((r) => onRejectRequest?.(r))}>
                          {t("meetingScreen.rejectAllPresenterRequests", { n: permissionRequests.length })}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className={cn(listHeight, "overflow-y-auto overscroll-contain p-3 space-y-2 bg-slate-50/50 dark:bg-muted/50")}>
                    {permissionRequests.map((request, index) => {
                      const asked = ASKED_FOR[request.kind]
                      return (
                        <div key={`${request.kind}:${request.id}`} className={rowClass} style={{ animationDelay: `${index * 50}ms` }}>
                          <div className="relative shrink-0">
                            <div className={avatarClass}>{request.name.slice(0, 2).toUpperCase()}</div>
                            <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full border border-border bg-card">
                              <asked.icon className="h-3 w-3 text-primary" />
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 dark:text-foreground/80 truncate">{request.name}</p>
                            <p className="text-xs text-slate-500 dark:text-muted-foreground truncate" title={t(asked.body)}>{t(asked.body)}</p>
                            {request.email && (
                              <p className="text-[11px] text-slate-400 dark:text-muted-foreground truncate">{request.email}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 opacity-80 group-hover:opacity-100 transition-opacity">
                            <Button size="sm" variant="ghost" onClick={() => onRejectRequest?.(request)} className={denyClass}
                              aria-label={t("dialogs.reject")} title={t("dialogs.reject")}>
                              <X className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant={'default'} onClick={() => onApproveRequest?.(request)} className="h-9 w-9 rounded-lg"
                              aria-label={t("dialogs.approve")} title={t("dialogs.approve")}>
                              <Check className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
